import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, GitBranch } from 'lucide-react';
import { Modal } from '../ui/Modal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import AccountAvatar from '../git/AccountAvatar';
import { GitAccountSelect, RepoSearchSelect, BranchSearchSelect, RepoOwnerSelect, RepoAccessNotices, reposForOwner } from '../git/RepoPickerParts';
import { repoRefFromInput } from '../../graph/repoRefs';
import { setActiveTab } from '../../store/uiModalStore';
import useRepoSources from './useRepoSources';
import './LinkRepoModal.css';

async function pickFolder() {
  try {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const picked = await open({ directory: true, multiple: false });
    return typeof picked === 'string' ? picked : null;
  } catch {
    return null;
  }
}

const label = { display: 'block', marginBottom: '0.375rem' };
const PROVIDER_NAMES = { github: 'GitHub', gitlab: 'GitLab', gitea: 'Gitea' };

/**
 * "Link repository" on a project page (card 5c626304): a code repository by
 * its URL, or — personal projects only — a local clone folder. Saves a
 * reference, never a credential: a URL with a password is refused, a user
 * name in an https URL is dropped (repoRefs.js).
 *
 * With a git account connected on the Git page (GitHub, GitLab, Gitea …) it
 * works like "Clone a repository" (owner, 26.10.2026): the account, a live-
 * searchable list of its repositories and then the repository's branches;
 * choosing fills URL, name, default branch and description. "Enter a URL
 * instead" and the local clone stay. Without an account — or when the account
 * cannot answer (expired sign-in, rate limit, offline) — the plain fields.
 */
export default function LinkRepoModal({ allowFolder, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({ url: '', path: '', name: '', branch: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('repos'); // 'repos' | 'url'
  const [selected, setSelected] = useState(null); // the picked repository
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const src = useRepoSources();
  const connected = Boolean(src.sources?.length);
  const picking = connected && mode === 'repos' && !src.reposError;

  // An account that cannot list (expired token, rate limit, offline): fall back to typing.
  useEffect(() => {
    if (src.reposError) setSelected(null);
  }, [src.reposError]);

  // Live check: what will be saved, or why it cannot be.
  const preview = useMemo(() => {
    if (!draft.url.trim() && !draft.path.trim()) return null;
    try {
      return { ref: repoRefFromInput(draft) };
    } catch (err) {
      return { error: err.message };
    }
  }, [draft]);

  const submit = async (e) => {
    e.preventDefault();
    if (!preview?.ref || busy) return;
    setBusy(true);
    try {
      await onSubmit(draft);
    } finally {
      setBusy(false);
    }
  };

  const chooseFolder = async () => {
    const path = await pickFolder();
    if (path) {
      setSelected(null);
      setMode('url');
      setDraft((d) => ({ ...d, path, url: '' }));
    }
  };

  const chooseRepo = (_url, repo) => {
    setSelected(repo);
    setDraft((d) => ({
      ...d,
      url: repo.url,
      path: '',
      name: repo.name,
      branch: repo.defaultBranch || '',
      description: d.description || repo.description || '',
    }));
    src.loadBranches(repo);
  };

  const changeSource = (key) => {
    src.setSourceKey(key);
    setSelected(null);
    setDraft((d) => ({ ...d, url: '', branch: '' }));
  };

  const changeMode = (next) => {
    setMode(next);
    setSelected(null);
  };

  const openGitPage = () => {
    onClose();
    setActiveTab('git');
  };

  const providerName = PROVIDER_NAMES[src.source?.provider] || src.source?.label || '';
  const errorKind = src.reposError?.kind;

  return (
    <Modal isOpen onClose={onClose} title={t('projects.repo.linkTitle')}>
      <form className="link-repo-form flex flex-col" style={{ gap: '0.875rem' }} onSubmit={submit} data-testid="link-repo-form">
        {src.sources === null && (
          <p className="link-repo-wrap pj-result-meta" data-testid="repo-accounts-loading">
            {t('projects.repo.lookingForAccounts')}
          </p>
        )}
        {connected && (
          <div>
            <span className="modal-logo-label" style={label}>
              {t('projects.repo.account')}
            </span>
            <div className="link-repo-account flex items-center gap-2" data-testid="repo-account">
              <AccountAvatar account={src.account} size={16} />
              <GitAccountSelect
                sources={src.sources}
                value={src.sourceKey}
                onChange={changeSource}
                disabled={busy}
                label={t('projects.repo.account')}
              />
            </div>
          </div>
        )}
        {connected && (
          <div>
            <span className="modal-logo-label" style={label}>
              {t('projects.repo.repository')}
            </span>
            <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
              <CompactSelectPicker
                value={mode}
                onChange={changeMode}
                options={[
                  { value: 'repos', label: t('projects.repo.modeRepos') },
                  { value: 'url', label: t('projects.repo.modeUrl') },
                ]}
                aria-label={t('projects.repo.repository')}
              />
            </div>
            {picking && (
              <div style={{ marginTop: '0.5rem' }} data-testid="repo-picker">
                <RepoOwnerSelect
                  owners={src.owners}
                  value={src.ownerKey}
                  onChange={src.setOwnerKey}
                  allText={t('projects.repo.ownerAll')}
                  label={t('projects.repo.owner')}
                  disabled={busy}
                />
                <RepoSearchSelect
                  repos={reposForOwner(src.repos, src.ownerKey, selected?.url)}
                  value={selected?.url || ''}
                  onChange={chooseRepo}
                  loading={src.reposLoading}
                  providerId={src.source?.provider}
                  login={src.account?.login || ''}
                  orgText={t('projects.repo.org')}
                  emptyText={t('projects.repo.noRepos')}
                  noMatchText={t('projects.repo.noRepoMatch')}
                  placeholder={t('projects.repo.selectRepo')}
                  searchPlaceholder={t('projects.repo.searchRepos')}
                  selectedText={t('projects.repo.selected')}
                />
                {!src.reposLoading && (
                  <RepoAccessNotices notices={src.notices} ownerKey={src.ownerKey} t={t} providerName={providerName} />
                )}
              </div>
            )}
          </div>
        )}
        {errorKind && (
          <div className="graph-error" role="alert" data-testid="repo-load-error">
            {t(`projects.repo.loadError.${errorKind}`, { provider: providerName })}
          </div>
        )}
        {!picking && (
          <div>
            <label className="modal-logo-label" htmlFor="repo-url" style={label}>
              {t('projects.repo.url')}
            </label>
            <input
              id="repo-url"
              type="text"
              autoFocus
              className="modal-input modal-input-flat"
              placeholder="https://github.com/team/api.git"
              value={draft.url}
              onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value, path: e.target.value ? '' : d.path }))}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}
        {allowFolder && (
          <div className="link-repo-row" style={{ marginTop: '-0.375rem' }}>
            <button type="button" className="gd-btn" onClick={chooseFolder} data-testid="repo-choose-folder">
              <FolderOpen size={13} /> {t('projects.repo.chooseFolder')}
            </button>
            {draft.path && <span className="link-repo-truncate pj-result-meta">{draft.path}</span>}
          </div>
        )}
        {src.sources && (!connected || errorKind === 'auth') && (
          <div className="link-repo-hint" data-testid="repo-connect-hint">
            <span className="link-repo-hint__text">{t('projects.repo.connectHint')}</span>
            <button type="button" className="gd-btn link-repo-hint__action" onClick={openGitPage}>
              {t('projects.repo.connectAction')}
            </button>
          </div>
        )}
        <div>
          <label className="modal-logo-label" htmlFor="repo-name" style={label}>
            {t('projects.name')}
          </label>
          <input
            id="repo-name"
            type="text"
            className="modal-input modal-input-flat"
            placeholder={preview?.ref?.name || t('projects.repo.namePlaceholder')}
            value={draft.name}
            onChange={set('name')}
          />
        </div>
        <div>
          <label className="modal-logo-label" htmlFor="repo-branch" style={label}>
            {t('projects.repo.branch')}
          </label>
          {picking && selected && !src.branchesError ? (
            <div data-testid="branch-picker">
              <BranchSearchSelect
                branches={src.branches}
                value={draft.branch}
                onChange={(name) => setDraft((d) => ({ ...d, branch: name }))}
                loading={src.branchesLoading}
                defaultBranch={selected.defaultBranch}
                defaultText={t('projects.repo.defaultMarker')}
                emptyText={t('projects.repo.noBranches')}
                noMatchText={t('projects.repo.noBranchMatch')}
                placeholder={t('projects.repo.selectBranch')}
                searchPlaceholder={t('projects.repo.searchBranches')}
              />
            </div>
          ) : (
            <input
              id="repo-branch"
              type="text"
              className="modal-input modal-input-flat"
              placeholder="main"
              value={draft.branch}
              onChange={set('branch')}
              spellCheck={false}
            />
          )}
          {picking && selected && src.branchesError && (
            <p className="link-repo-wrap pj-result-meta" style={{ marginTop: '0.375rem' }} data-testid="branch-load-error">
              {t('projects.repo.branchLoadError')}
            </p>
          )}
        </div>
        <div>
          <label className="modal-logo-label" htmlFor="repo-description" style={label}>
            {t('projects.description')}
          </label>
          <textarea
            id="repo-description"
            className="modal-input modal-input-flat"
            style={{ resize: 'vertical' }}
            rows={2}
            placeholder={t('projects.descriptionPlaceholder')}
            value={draft.description}
            onChange={set('description')}
          />
        </div>
        {preview?.error && (
          <div className="graph-error" role="alert">
            {preview.error}
          </div>
        )}
        {preview?.ref && (
          <div className="link-repo-row pj-result-meta" data-testid="repo-preview">
            <GitBranch size={13} aria-hidden className="flex-shrink-0" />
            <span className="link-repo-truncate">
              {[preview.ref.name, preview.ref.host || t('projects.repo.localFolder'), preview.ref.branch].filter(Boolean).join(' · ')}
            </span>
          </div>
        )}
        {!allowFolder && <p className="link-repo-wrap pj-result-meta">{t('projects.repo.teamHint')}</p>}
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!preview?.ref || busy}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {t('projects.repo.link')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
